import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Form, Input, InputNumber, message, Modal, Row, Select, Tooltip, Switch, Button, Checkbox } from "antd";
import { DownOutlined, QuestionCircleOutlined, UpOutlined, PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import Service, { ElAdminEventLogService } from "../services";
import {
  CME_TRADING_PAIRS,
  EXCHANGE_INSTRUMENT,
  TRADING_PAIRS,
  CONTROL_ACTION_TYPE,
  PORTFOLIO_OPTIMIZATION_PAIRS,
  TMUX_OPERATION_DELAY,
  formatMultiAssetConfig,
  isArbitrage,
  MODEL_TYPE,
  TRADE_STATUS,
} from "../utils";
import { availableTimeIntervals } from "../pages/main/ModelPerformance";
import styles from "./index.module.scss";
import { naturalSortSessions, isUsdsFuture, isCoinFuture } from "../utils";
import { round } from "lodash";

const { Option } = Select;
const layout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 17 },
};

// Alpha Forge modes run as standalone sessions in the execution gate and draw from
// the same account allocation pool, so they must pass the total-allocation check
const ALPHA_FORGE_MODES = ["crypto_v4_2h_signal_redis", "crypto_v5a_30m_signal_redis"];

// Trade Form Field Names - keys match values for direct visibility in code
export const TRADE_FORM_FIELDS = {
  // Core Identity Fields
  exchange: "exchange",
  account: "account",
  pair: "pair",
  sessionName: "sessionName",
  contactNumbersStr: "contactNumbersStr",

  // Position & Allocation
  balanceAllocationRatio: "balanceAllocationRatio",
  longRatio: "longRatio",
  shortRatio: "shortRatio",
  proxy: "proxy",

  // Circuit Breaker Settings
  enableCircuitBreaker: "enableCircuitBreaker",
  circuitBreakerDailyThreshold: "circuitBreakerDailyThreshold",
  circuitBreakerWeeklyThreshold: "circuitBreakerWeeklyThreshold",
  circuitBreakerMonthlyThreshold: "circuitBreakerMonthlyThreshold",

  // Execution Modes
  stateManagerMode: "stateManagerMode",
  executeMode: "executeMode",
  modelUpdateMode: "modelUpdateMode",

  // Model Configuration
  overrideModelIntervals: "overrideModelIntervals",
  modelIntervals: "modelIntervals",
  loggerNamespaces: "loggerNamespaces",

  // Prediction Settings
  enableEonModelPredictor: "enableEonModelPredictor",
  predictionSource: "predictionSource",
  executionDelay: "executionDelay",

  // Multi-Asset Collateral (form inputs)
  ma: "ma",
  low: "low",
  // Multi-Asset Collateral (formatted for API)
  multiAssetConfig: "multiAssetConfig",

  // Trading Hours (formatted JSON string)
  tradingHoursParams: "tradingHoursParams",

  // Arbitrage Settings (formatted JSON string)
  arbitrageParams: "arbitrageParams",
  // Arbitrage form inputs (not sent to API)
  spotTargetAmt: "spotTargetAmt",
  futureTargetAmt: "futureTargetAmt",
  maxVolumePercent: "maxVolumePercent",

  // Binance Specific
  minimumBNB: "minimumBNB",

  // Spot Trading Asset Allocation
  minBaseAssetRatio: "minBaseAssetRatio",
  maxBaseAssetRatio: "maxBaseAssetRatio",

  // CME Specific
  leverage: "leverage",
  walletBalance: "walletBalance",
  maxContractAmt: "maxContractAmt",
  extendedHoursRatio: "extendedHoursRatio",
  dailyMarketClosedRatio: "dailyMarketClosedRatio",
  weekendMarketClosedRatio: "weekendMarketClosedRatio",

  // Order Router
  orderRouter: "orderRouter",
};

// Fields excluded from execution APIs (form-only fields that need transformation)
export const EXCLUDED_FROM_API = [
  TRADE_FORM_FIELDS.ma,
  TRADE_FORM_FIELDS.low,
  TRADE_FORM_FIELDS.spotTargetAmt,
  TRADE_FORM_FIELDS.futureTargetAmt,
  TRADE_FORM_FIELDS.maxVolumePercent,
];

// Fields that closePosition mutation doesn't accept (display/status only)
export const MUTATION_UNSUPPORTED_FIELDS = [
  TRADE_FORM_FIELDS.circuitBreakerDailyThreshold,
  TRADE_FORM_FIELDS.circuitBreakerWeeklyThreshold,
  TRADE_FORM_FIELDS.circuitBreakerMonthlyThreshold,
  TRADE_FORM_FIELDS.modelUpdateMode,
  TRADE_FORM_FIELDS.extendedHoursRatio,
  TRADE_FORM_FIELDS.dailyMarketClosedRatio,
  TRADE_FORM_FIELDS.weekendMarketClosedRatio,
];

const exchangeArr = [
  {
    label: "Binance USD-M future",
    value: EXCHANGE_INSTRUMENT.binance_usds_future,
  },
  {
    label: "Binance Coin-M future",
    value: EXCHANGE_INSTRUMENT.binance_coin_future,
  },
  {
    label: "Binance Funding Rate Arbitrage",
    value: EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage,
  },
  {
    label: "Binance Spot",
    value: EXCHANGE_INSTRUMENT.binance_spot,
  },
  {
    label: "OKX USD-M future",
    value: EXCHANGE_INSTRUMENT.okx_usds_future,
  },
  {
    label: "OKX Funding Rate Arbitrage",
    value: EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage,
  },
  {
    label: "CME future",
    value: EXCHANGE_INSTRUMENT.cme_tradestation_future,
  },
  {
    label: "Crypto.com USD-M future",
    value: EXCHANGE_INSTRUMENT.cryptocom_usds_future,
  },
  {
    label: "Route28 USD-M future",
    value: EXCHANGE_INSTRUMENT.route28_usds_future,
  },
];

const TradeModal = ({ tradeModelDetails, siblingAccountStatuses, username, closeModal }) => {
  const [enableCircuitBreaker, setEnableCircuitBreaker] = useState(tradeModelDetails.parsedConfigs.enableCircuitBreaker);
  const [overrideModelIntervals, setOverrideModelIntervals] = useState(tradeModelDetails.parsedConfigs.overrideModelIntervals);
  const [balanceAllocationRatioMessage, setBalanceAllocationRatioMessage] = useState("");
  const [selectedPairs, setSelectedPairs] = useState([tradeModelDetails.symbol]);
  const [isAllPortfolioOptimizationPairsSelected, setIsAllPortfolioOptimizationPairsSelected] = useState(false);
  const [bypassAllocationLimit, setBypassAllocationLimit] = useState(false);

  const [form] = Form.useForm();
  const balanceAllocationRatio = Form.useWatch(TRADE_FORM_FIELDS.balanceAllocationRatio, form);
  const [isAdvancedOptionsVisible, setAdvancedOptionsVisible] = useState(false);
  const exchange = Form.useWatch(TRADE_FORM_FIELDS.exchange, form);
  const spotTargetAmt = Form.useWatch(TRADE_FORM_FIELDS.spotTargetAmt, form);
  const futureTargetAmt = Form.useWatch(TRADE_FORM_FIELDS.futureTargetAmt, form);
  const pair = Form.useWatch(TRADE_FORM_FIELDS.pair, form);
  const isTradeAll = tradeModelDetails.isTradeAll;
  const isCoinFutureMode = isCoinFuture(exchange);
  const isArbitrageMode = isArbitrage(exchange);
  const isStateManagerModeSupported = [EXCHANGE_INSTRUMENT.binance_usds_future, EXCHANGE_INSTRUMENT.okx_usds_future].includes(exchange);
  const shouldShowTradingHoursRatio =
    (isUsdsFuture(exchange) || isCoinFuture(exchange)) && exchange !== EXCHANGE_INSTRUMENT.cme_tradestation_future;

  const circuitBreakerIsSupported = [
    EXCHANGE_INSTRUMENT.binance_usds_future,
    EXCHANGE_INSTRUMENT.binance_coin_future,
    EXCHANGE_INSTRUMENT.okx_usds_future,
    EXCHANGE_INSTRUMENT.cme_tradestation_future,
    EXCHANGE_INSTRUMENT.cryptocom_usds_future,
  ].includes(tradeModelDetails.exchange);
  // cme doesn't support balance allocation. instead, it requires a fixed balance for the session
  const isBalanceAllocationSupported = ![
    EXCHANGE_INSTRUMENT.cme_tradestation_future,
    EXCHANGE_INSTRUMENT.route28_usds_future,
    EXCHANGE_INSTRUMENT.binance_coin_future,
    EXCHANGE_INSTRUMENT.binance_spot,
    EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage,
    EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage,
  ].includes(exchange);
  const isFixedBalanceSupported = exchange === EXCHANGE_INSTRUMENT.route28_usds_future;

  let tradingPairs;
  if (exchange === EXCHANGE_INSTRUMENT.cme_tradestation_future) {
    tradingPairs = CME_TRADING_PAIRS;
  } else if (
    exchange === EXCHANGE_INSTRUMENT.binance_usds_future ||
    exchange === EXCHANGE_INSTRUMENT.binance_funding_rate_arbitrage ||
    exchange === EXCHANGE_INSTRUMENT.okx_funding_rate_arbitrage ||
    exchange === EXCHANGE_INSTRUMENT.route28_usds_future
  ) {
    tradingPairs = TRADING_PAIRS;
  } else {
    tradingPairs = TRADING_PAIRS.map((pair) => pair.replace("1000", ""));
  }

  const cmeForm = _createCMEForm(exchange, tradeModelDetails, pair);

  const tradingSiblings = useMemo(() => {
    const siblings = [];
    const siblingData = isTradeAll ? tradeModelDetails.siblingAccountStatuses : siblingAccountStatuses;
    if (siblingData) {
      siblings.push(
        ...siblingData
          .filter(({ status }) => status === TRADE_STATUS.trading)
          .map(({ account, parsedConfigs, symbol, exchange }) => ({
            account,
            balanceAllocationRatio: parsedConfigs.balanceAllocationRatio,
            symbol,
            exchange,
          }))
      );
    }
    return siblings;
  }, [siblingAccountStatuses, tradeModelDetails.siblingAccountStatuses, isTradeAll]);

  // Watch for exchange changes
  useEffect(() => {
    if (isCoinFuture(exchange)) {
      const pairs = isTradeAll
        ? tradeModelDetails.siblingAccountStatuses
            ?.filter((status) => isCoinFuture(status.exchange) && status.status === TRADE_STATUS.trading)
            .map((status) => status.symbol)
        : tradeModelDetails.symbol;

      setSelectedPairs(Array.isArray(pairs) ? pairs : [pairs]);
      form.setFieldsValue({
        [TRADE_FORM_FIELDS.pair]: pairs,
        [TRADE_FORM_FIELDS.balanceAllocationRatio]: 100,
        [TRADE_FORM_FIELDS.stateManagerMode]: "standalone",
      });
    }
  }, [exchange, tradeModelDetails, form, isTradeAll]);

  //updateSelectedPairs for non-coin futures
  const updateSelectedPairs = useCallback(() => {
    if (tradeModelDetails.isTradeAll && !isCoinFuture(tradeModelDetails.exchange)) {
      const tradingSymbols = new Set([tradeModelDetails.symbol]);
      tradingSiblings
        ?.filter((sibling) => !isCoinFuture(sibling.exchange))
        .forEach((sibling) => {
          tradingSymbols.add(sibling.symbol);
        });
      const updatedPairs = Array.from(tradingSymbols);
      setSelectedPairs(updatedPairs);
      form.setFieldsValue({
        [TRADE_FORM_FIELDS.pair]: updatedPairs,
        [TRADE_FORM_FIELDS.exchange]: tradeModelDetails.exchange,
      });
    }
  }, [tradeModelDetails, tradingSiblings, form]);

  useEffect(() => {
    if (tradeModelDetails.isTradeAll) {
      updateSelectedPairs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeModelDetails.isTradeAll]);
  // Only run when modal opens (isTradeAll becomes true), not when updateSelectedPairs changes
  // This prevents overwriting user's manual pair selection

  const handlePairChange = (values) => {
    if (isTradeAll) {
      // Multiple selection mode
      setSelectedPairs(Array.isArray(values) ? values : [values]);
      form.setFieldValue(TRADE_FORM_FIELDS.pair, values);
    } else {
      // Single selection mode - ensure only one value
      const singleValue = Array.isArray(values) ? values[values.length - 1] : values;
      setSelectedPairs([singleValue]);
      form.setFieldValue(TRADE_FORM_FIELDS.pair, singleValue);
    }
  };

  const getAvailableSessions = () => {
    const idleUsdSessions = [];
    const idleCoinSessions = [];

    if (tradeModelDetails.siblingAccountStatuses) {
      tradeModelDetails.siblingAccountStatuses.forEach((status) => {
        if (status.status !== TRADE_STATUS.trading && status.status !== TRADE_STATUS.paused) {
          const session = {
            account: status.account,
            sessionName: status.sessionName,
            exchange: status.exchange,
            symbol: status.symbol,
          };

          if (isUsdsFuture(status.exchange)) {
            idleUsdSessions.push(session);
          } else if (isCoinFuture(status.exchange)) {
            idleCoinSessions.push(session);
          }
        }
      });
    }

    return {
      idleUsdSessions: idleUsdSessions.sort((a, b) => naturalSortSessions(a.account, b.account)),
      idleCoinSessions: idleCoinSessions.sort((a, b) => naturalSortSessions(a.account, b.account)),
    };
  };
  const { idleUsdSessions, idleCoinSessions } = getAvailableSessions();

  const validateTotalAllocation = () => {
    const currentStateManagerMode = form.getFieldValue(TRADE_FORM_FIELDS.stateManagerMode);
    const selectedPairs = Array.isArray(form.getFieldValue(TRADE_FORM_FIELDS.pair))
      ? form.getFieldValue(TRADE_FORM_FIELDS.pair)
      : [form.getFieldValue(TRADE_FORM_FIELDS.pair)];

    if (currentStateManagerMode === "standalone-rule-based-portfolio-optimization-v1" || bypassAllocationLimit) {
      return Promise.resolve();
    }
    if (
      (currentStateManagerMode === "standalone" || ALPHA_FORGE_MODES.includes(currentStateManagerMode)) &&
      tradeModelDetails.actionType === "Start Trade"
    ) {
      const usedAllocation = tradingSiblings.reduce(
        (acc, cur) => (!isCoinFuture(cur.exchange) && !selectedPairs.includes(cur.symbol) ? acc + cur.balanceAllocationRatio : acc),
        0
      );
      const remainingAllocation = 100 - usedAllocation;
      const allocationPerPair = !isTradeAll ? remainingAllocation : remainingAllocation / selectedPairs.length;
      setBalanceAllocationRatioMessage(`Maximum allocation per pair: ${allocationPerPair.toFixed(2)}%`);
      if (balanceAllocationRatio > allocationPerPair) {
        return Promise.reject(`Maximum allocation per pair: ${allocationPerPair.toFixed(2)}%`);
      }
    }
    return Promise.resolve();
  };

  const handleSubmit = async (formValues) => {
    try {
      const createTradeStatusDetails = (params, action) => ({
        exchange: params.exchange,
        account: params.account,
        symbol: params.pair,
        actionType: CONTROL_ACTION_TYPE[action],
        marginBalance: params.marginBalance,
        formConfigParams: {
          exchange: params.exchange,
          account: params.account,
          pair: params.pair,
        },
        configs: JSON.stringify({
          ...params,
          multiAssetConfig: formatMultiAssetConfig({
            ma: params.ma,
            low: params.low,
          }),
        }),
      });
      const executeTradeAction = async (params, action) => {
        // remove params that are not needed for the execution
        const { ma, low, spotTargetAmt, futureTargetAmt, maxVolumePercent, ...cleanParams } = params;
        // construct the execution params
        const executionParams = {
          ...cleanParams,
          multiAssetConfig: formatMultiAssetConfig({
            ma: params.ma,
            low: params.low,
          }),
          tradingHoursParams: parseTradingHoursParamsToString(),
          arbitrageParams: parseArbitrageParamsToString(),
        };
        if (action === "Start Trade") {
          await Service.startTradeExecution(executionParams);
          message.success(`Started trading ${params.pair} on ${params.sessionName}`);
        } else if (action === "Close Position") {
          await Service.closePositionTradeExecution(executionParams);
          message.success(`Position closed for ${params.pair} on ${params.sessionName}`);
        }
        const tradeStatusDetails = createTradeStatusDetails(params, action);
        await ElAdminEventLogService.saveTradeActionEvent(tradeStatusDetails, username);
      };
      // For non-TradeAll cases
      if (!isTradeAll) {
        await executeTradeAction(formValues, tradeModelDetails.actionType);
        closeModal();
        return;
      }
      // For TradeAll cases - implement priority-based session matching
      const allSessions = tradeModelDetails.siblingAccountStatuses.filter((s) =>
        isCoinFutureMode ? isCoinFuture(s.exchange) : isUsdsFuture(s.exchange)
      );
      const errors = [];
      const alreadyAssigned = new Set();

      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      // Helper function to find session for a pair with priority-based matching
      const findSessionForPair = (pair) => {
        const statusPriority = [
          TRADE_STATUS.trading,
          TRADE_STATUS.waiting,
          TRADE_STATUS.error,
          TRADE_STATUS.paused,
          TRADE_STATUS.positionClosed,
        ];

        // Try matching by symbol in priority order
        for (const status of statusPriority) {
          const candidates = allSessions
            .filter((s) => s.symbol === pair && s.status === status && !alreadyAssigned.has(s.key || s.sessionName))
            .sort(naturalSortSessions);
          if (candidates.length > 0) {
            return candidates[0];
          }
        }

        // No match - find first available free session (any status, sorted by natural order)
        const freeSessions = allSessions.filter((s) => !alreadyAssigned.has(s.key || s.sessionName)).sort(naturalSortSessions);
        return freeSessions.length > 0 ? freeSessions[0] : null;
      };

      for (const pair of selectedPairs) {
        const sessionInfo = findSessionForPair(pair);
        if (!sessionInfo) {
          errors.push(`No available session for ${pair}`);
          continue;
        }

        // Mark session as assigned
        alreadyAssigned.add(sessionInfo.key || sessionInfo.sessionName);

        const formParams = {
          ...formValues,
          account: sessionInfo.account,
          sessionName: sessionInfo.sessionName,
          pair,
          balanceAllocationRatio: isCoinFutureMode ? 100 : Math.max(formValues.balanceAllocationRatio, 2),
          stateManagerMode: isCoinFutureMode ? "standalone" : formValues.stateManagerMode,
          multiAssetConfig: formatMultiAssetConfig({
            ma: formValues.ma,
            low: formValues.low,
          }),
          tradingHoursParams: parseTradingHoursParamsToString(),
          arbitrageParams: parseArbitrageParamsToString(),
        };

        try {
          await executeTradeAction(formParams, "Start Trade");
          await delay(TMUX_OPERATION_DELAY); // Add 0.5 second delay between each pair
        } catch (error) {
          errors.push(`Failed to start ${pair}: ${error.message}`);
        }
      }
      if (errors.length > 0) {
        message.warning(errors.join("\n"));
      }
      closeModal();
    } catch (error) {
      console.error("Submit Error:", error);
      message.error(`Operation failed: ${error.message}`);
    }
  };

  const handleBulkSelect = () => {
    if (isAllPortfolioOptimizationPairsSelected) {
      // Unselect all pairs
      setSelectedPairs([tradeModelDetails.symbol]);
      form.setFieldValue(TRADE_FORM_FIELDS.pair, [tradeModelDetails.symbol]);
      setIsAllPortfolioOptimizationPairsSelected(false);
    } else {
      const formattedOptimizationPairs =
        exchange === EXCHANGE_INSTRUMENT.cme_tradestation_future
          ? PORTFOLIO_OPTIMIZATION_PAIRS.filter((pair) => ["BTC/USD", "ETH/USD"].includes(pair))
          : exchange === EXCHANGE_INSTRUMENT.binance_usds_future || exchange === EXCHANGE_INSTRUMENT.route28_usds_future
          ? PORTFOLIO_OPTIMIZATION_PAIRS
          : PORTFOLIO_OPTIMIZATION_PAIRS.map((pair) => pair.replace("1000", ""));
      const totalAvailableSessions = tradeModelDetails.siblingAccountStatuses.filter((s) => !isCoinFuture(s.exchange))?.length;
      if (formattedOptimizationPairs.length <= totalAvailableSessions) {
        setSelectedPairs(formattedOptimizationPairs);
        form.setFieldValue(TRADE_FORM_FIELDS.pair, formattedOptimizationPairs);
        setIsAllPortfolioOptimizationPairsSelected(true);
      } else {
        message.warning(`Not enough available sessions. Need more sessions for portfolio optimization.`);
      }
    }
  };

  const parseTradingHoursParamsToString = () => {
    const tradingHoursParams = form.getFieldValue(TRADE_FORM_FIELDS.tradingHoursParams) || [];
    if (tradingHoursParams.length === 0) {
      return "";
    }
    return JSON.stringify(
      tradingHoursParams.map((originalParams) => {
        const params = { ...originalParams }; // avoid mutating the original object
        params.tradingHoursRatio = round(params.tradingHoursRatio / 100, 2);
        params.nonTradingHoursRatio = round(params.nonTradingHoursRatio / 100, 2);
        return params;
      })
    );
  };

  const parseArbitrageParamsToString = () => {
    const spotTargetAmt = form.getFieldValue(TRADE_FORM_FIELDS.spotTargetAmt) || 0;
    const futureTargetAmt = form.getFieldValue(TRADE_FORM_FIELDS.futureTargetAmt) || 0;
    const maxVolumePercent = form.getFieldValue(TRADE_FORM_FIELDS.maxVolumePercent) || 2;

    return JSON.stringify({
      spotTargetAmt,
      futureTargetAmt,
      maxVolumePercent,
    });
  };

  // Get the initial balance allocation ratio
  const initialBalanceAllocationRatio = useMemo(() => {
    return tradeModelDetails.parsedConfigs?.balanceAllocationRatio ?? 100;
  }, [tradeModelDetails]);

  return (
    <Modal
      width={640}
      open={true}
      title={tradeModelDetails.actionType}
      okText="Confirm"
      cancelText="Cancel"
      onCancel={closeModal}
      onOk={async () => {
        try {
          const values = await form.validateFields();
          await handleSubmit(values);
        } catch (error) {
          console.error("Validation Failed:", error);
          message.error(error.message || String(error));
        }
      }}
    >
      <Form
        {...layout}
        form={form}
        initialValues={{
          [TRADE_FORM_FIELDS.exchange]: tradeModelDetails.exchange,
          [TRADE_FORM_FIELDS.account]: tradeModelDetails.account,
          [TRADE_FORM_FIELDS.pair]: tradeModelDetails.accountSymbol && tradeModelDetails.accountSymbol.split("_").splice(-1)[0],
          [TRADE_FORM_FIELDS.sessionName]: tradeModelDetails.sessionName,
          [TRADE_FORM_FIELDS.contactNumbersStr]: tradeModelDetails.contactNumbers && tradeModelDetails.contactNumbers.join(","),
          [TRADE_FORM_FIELDS.longRatio]: tradeModelDetails.parsedConfigs?.longRatio ?? 100,
          [TRADE_FORM_FIELDS.shortRatio]: tradeModelDetails.parsedConfigs?.shortRatio ?? 100,
          [TRADE_FORM_FIELDS.proxy]: tradeModelDetails.parsedConfigs?.proxy ?? "default",
          [TRADE_FORM_FIELDS.balanceAllocationRatio]: tradeModelDetails.parsedConfigs?.balanceAllocationRatio ?? 100,
          [TRADE_FORM_FIELDS.enableEonModelPredictor]: tradeModelDetails.parsedConfigs?.enableEonModelPredictor ?? "false",
          [TRADE_FORM_FIELDS.executionDelay]: tradeModelDetails.parsedConfigs?.executionDelay,
          [TRADE_FORM_FIELDS.predictionSource]: tradeModelDetails.parsedConfigs?.predictionSource,
          [TRADE_FORM_FIELDS.stateManagerMode]: tradeModelDetails.parsedModes?.stateManagerMode,
          [TRADE_FORM_FIELDS.executeMode]: tradeModelDetails.parsedModes?.executeMode,
          [TRADE_FORM_FIELDS.enableCircuitBreaker]: enableCircuitBreaker,
          [TRADE_FORM_FIELDS.circuitBreakerDailyThreshold]: tradeModelDetails.parsedConfigs?.circuitBreakerDailyThreshold ?? 20,
          [TRADE_FORM_FIELDS.circuitBreakerWeeklyThreshold]: tradeModelDetails.parsedConfigs?.circuitBreakerWeeklyThreshold ?? 25,
          [TRADE_FORM_FIELDS.circuitBreakerMonthlyThreshold]: tradeModelDetails.parsedConfigs?.circuitBreakerMonthlyThreshold ?? 30,
          [TRADE_FORM_FIELDS.overrideModelIntervals]: overrideModelIntervals,
          [TRADE_FORM_FIELDS.modelIntervals]: tradeModelDetails.parsedConfigs?.modelIntervals,
          [TRADE_FORM_FIELDS.loggerNamespaces]: tradeModelDetails.parsedConfigs?.loggerNamespaces,
          [TRADE_FORM_FIELDS.minimumBNB]: tradeModelDetails.parsedConfigs?.minimumBNB ?? 0,
          [TRADE_FORM_FIELDS.minBaseAssetRatio]: tradeModelDetails.parsedConfigs?.minBaseAssetRatio ?? 30,
          [TRADE_FORM_FIELDS.maxBaseAssetRatio]: tradeModelDetails.parsedConfigs?.maxBaseAssetRatio ?? 70,
          ...(tradeModelDetails.arbitrageParams
            ? JSON.parse(tradeModelDetails.arbitrageParams)
            : {
                [TRADE_FORM_FIELDS.spotTargetAmt]: 0,
                [TRADE_FORM_FIELDS.futureTargetAmt]: 0,
                [TRADE_FORM_FIELDS.maxVolumePercent]: 2,
              }),
          ...cmeForm.initialValues,
          [TRADE_FORM_FIELDS.ma]: tradeModelDetails.parsedConfigs?.multiAssetConfig?.ma ?? 30,
          [TRADE_FORM_FIELDS.low]: tradeModelDetails.parsedConfigs?.multiAssetConfig?.low ?? 7,
          [TRADE_FORM_FIELDS.walletBalance]: tradeModelDetails.tradeAccountStatus?.walletBalance,
          [TRADE_FORM_FIELDS.leverage]: tradeModelDetails.leverage,
          [TRADE_FORM_FIELDS.tradingHoursParams]: tradeModelDetails.tradingHoursParams
            ? JSON.parse(tradeModelDetails.tradingHoursParams).map((param) => ({
                ...param,
                tradingHoursRatio: round(param.tradingHoursRatio * 100),
                nonTradingHoursRatio: round(param.nonTradingHoursRatio * 100),
              }))
            : [],
          [TRADE_FORM_FIELDS.orderRouter]: tradeModelDetails.parsedConfigs?.orderRouter ?? undefined,
        }}
      >
        {/* ------------- Mandatory Options Section Starts ------------- */}
        <Form.Item label="Executor name">{username}</Form.Item>
        <Form.Item label="Exchange" name={TRADE_FORM_FIELDS.exchange} rules={[{ required: true, message: "Please select exchange!" }]}>
          <Select showSearch placeholder="Please select exchange" onChange={() => {}} optionFilterProp="children" disabled>
            {exchangeArr.map((e, index) => (
              <Option key={index} value={e.value}>
                {e.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name={TRADE_FORM_FIELDS.account}
          label="Account"
          rules={[{ required: true, message: "Please input Account", type: "string" }]}
        >
          <Input placeholder="Please input Account" readOnly />
        </Form.Item>
        <Form.Item label="Pair" name={TRADE_FORM_FIELDS.pair} rules={[{ required: true, message: "Please select or enter trading-pair!" }]}>
          <Select
            mode={isTradeAll ? "tags" : undefined}
            showSearch
            placeholder={
              isTradeAll ? "Please select trading pairs or enter custom pairs" : "Please select trading pair or enter custom pair"
            }
            onChange={handlePairChange}
            defaultValue={isTradeAll ? undefined : tradeModelDetails.symbol}
            value={isTradeAll ? selectedPairs : selectedPairs[0]}
            optionFilterProp="children"
            maxTagCount="responsive"
            tokenSeparators={isTradeAll ? [","] : undefined}
            allowClear={!isTradeAll}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !isTradeAll) {
                const searchValue = e.target.value;
                if (searchValue && !tradingPairs.includes(searchValue) && (!selectedPairs.length || searchValue !== selectedPairs[0])) {
                  handlePairChange(searchValue);
                  e.preventDefault();
                }
              }
            }}
          >
            {[...tradingPairs].map((pair) => {
              const isDisabled =
                isTradeAll &&
                selectedPairs.length >=
                  tradeModelDetails.siblingAccountStatuses?.filter((s) =>
                    isCoinFutureMode ? isCoinFuture(s.exchange) : !isCoinFuture(s.exchange)
                  ).length &&
                !selectedPairs.includes(pair);
              return (
                <Option key={pair} value={pair} disabled={isDisabled}>
                  {pair}
                </Option>
              );
            })}
          </Select>
          {/* ) : (
            <Select
              showSearch
              placeholder="Please select trading-pair"
              onChange={() => {}}
              optionFilterProp="children"
            >
              {[...tradingPairs].map((pair) => (
                <Option key={pair} value={pair}>
                  {pair}
                </Option>
              ))}
            </Select>
          )} */}
        </Form.Item>
        {isTradeAll && !isCoinFutureMode && (
          <Form.Item label=" " colon={false}>
            <Button onClick={handleBulkSelect}>
              {isAllPortfolioOptimizationPairsSelected ? "Unselect All P/O Pairs" : "Select All P/O Pairs"}
            </Button>
          </Form.Item>
        )}
        {isFixedBalanceSupported && (
          <>
            <Form.Item
              name={TRADE_FORM_FIELDS.walletBalance}
              label="Fixed Session Balance"
              rules={[{ required: true, message: "Fixed balance for the session" }]}
            >
              <Input type="number" min={1000} step={1000} placeholder="session fixed balance" />
            </Form.Item>
            <Form.Item name={TRADE_FORM_FIELDS.leverage} label="Leverage" rules={[{ required: true, message: "Manual leverage settings" }]}>
              <Input type="number" min={1} max={4} placeholder="Manual leverage settings" />
            </Form.Item>
          </>
        )}
        {cmeForm.formItems}
        {exchange === EXCHANGE_INSTRUMENT.binance_spot && (
          <Form.Item
            label={
              <>
                Asset Allocation
                <Tooltip
                  title="Defines the asset allocation. For example, [-1: 30%] means when fully short, hold 30% of total value in coin (e.g. BTC). [1: 70%] means when fully long, hold 70% of total value in coin (e.g. BTC)."
                  color="#78909c"
                >
                  <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                </Tooltip>
              </>
            }
            style={{ marginBottom: 0 }}
          >
            <span
              style={{
                display: "inline-block",
                width: "100px",
                lineHeight: "32px",
                textAlign: "center",
              }}
            >
              position=-1:
            </span>
            <Form.Item name={TRADE_FORM_FIELDS.minBaseAssetRatio} style={{ display: "inline-block", width: "calc(50% - 100px)" }}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                max={100}
                step={10}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace("%", "")}
              />
            </Form.Item>
            <span
              style={{
                display: "inline-block",
                width: "100px",
                lineHeight: "32px",
                textAlign: "center",
              }}
            >
              position=1:
            </span>
            <Form.Item name={TRADE_FORM_FIELDS.maxBaseAssetRatio} style={{ display: "inline-block", width: "calc(50% - 100px)" }}>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                max={100}
                step={10}
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace("%", "")}
              />
            </Form.Item>
          </Form.Item>
        )}
        {/* ------------- Mandatory Options Section Ends ------------- */}

        {/* Arbitrage-specific fields */}
        {isArbitrageMode && (
          <>
            <Form.Item
              name={TRADE_FORM_FIELDS.spotTargetAmt}
              label="Spot Target Amt"
              tooltip="Final spot/margin holding amount (base asset). This is the absolute target regardless of current position."
              rules={[{ required: true, message: "Please input spot target amount" }]}
            >
              <InputNumber min={0} step={1} style={{ width: "100%" }} placeholder="Enter spot target amount" />
            </Form.Item>
            <Form.Item
              name={TRADE_FORM_FIELDS.futureTargetAmt}
              label="Future Target Amt"
              tooltip="Futures position target. Negative = short exposure, Positive = long exposure (uses contracts instead of spot to save fees)."
              rules={[{ required: true, message: "Please input future target amount (negative for short, positive for long)" }]}
            >
              <InputNumber step={1} style={{ width: "100%" }} placeholder="Negative = short, Positive = long" />
            </Form.Item>
            {(() => {
              const spot = spotTargetAmt || 0;
              const future = futureTargetAmt || 0;
              const totalLong = spot + Math.max(future, 0);
              const hedged = future < 0 ? Math.min(spot, Math.abs(future)) : 0;
              const netShort = future < 0 ? Math.max(Math.abs(future) - spot, 0) : 0;
              const netLong = totalLong - hedged;
              if (netLong > 0 && netShort === 0) {
                return (
                  <Form.Item wrapperCol={{ offset: 7, span: 17 }} style={{ marginTop: -16, marginBottom: 8 }}>
                    <span style={{ color: "#1890ff", fontSize: 12 }}>
                      Long exposure: {netLong}
                      {hedged > 0 ? ` (${hedged} hedged)` : ""}
                    </span>
                  </Form.Item>
                );
              }
              if (netShort > 0) {
                return (
                  <Form.Item wrapperCol={{ offset: 7, span: 17 }} style={{ marginTop: -16, marginBottom: 8 }}>
                    <span style={{ color: "#faad14", fontSize: 12 }}>
                      Short exposure: {netShort}
                      {hedged > 0 ? ` (${hedged} hedged)` : ""}
                    </span>
                  </Form.Item>
                );
              }
              return null;
            })()}
            <Form.Item
              name={TRADE_FORM_FIELDS.maxVolumePercent}
              label="Max Volume Percent"
              tooltip="Sets the arbitrage execution speed as a percentage of total market trading volume. For example, if the total BTC trading volume is 100 BTC per minute and you input 5%, the execution speed will be limited to 5 BTC per minute."
              rules={[{ required: true, message: "Please input max volume percent" }]}
            >
              <InputNumber
                min={1}
                max={100}
                step={1}
                style={{ width: "100%" }}
                placeholder="Enter max volume percent"
                formatter={(value) => `${value}%`}
                parser={(value) => value.replace("%", "")}
              />
            </Form.Item>
          </>
        )}

        {isBalanceAllocationSupported && !isCoinFutureMode && (
          <Form.Item
            label="Balance Allocation Ratio"
            name={TRADE_FORM_FIELDS.balanceAllocationRatio}
            help={
              <div>
                {balanceAllocationRatioMessage}
                <div style={{ marginTop: 8, marginBottom: 16 }}>
                  <Checkbox checked={bypassAllocationLimit} onChange={(e) => setBypassAllocationLimit(e.target.checked)}>
                    <Tooltip title="Enable to allow allocation ratio exceeding 100%. Use with caution.">
                      Bypass 100% allocation limit
                    </Tooltip>
                  </Checkbox>
                </div>
              </div>
            }
            rules={[
              {
                validator: validateTotalAllocation,
              },
            ]}
            initialValue={initialBalanceAllocationRatio}
          >
            <InputNumber
              min={1}
              max={bypassAllocationLimit ? 1000 : 100}
              step={5}
              style={{ width: "100%" }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
        )}
        {/* Trading Sessions */}
        {isCoinFutureMode
          ? // Show Coin-M Future Trading Sessions
            tradingSiblings?.filter((s) => isCoinFuture(s.exchange)).length > 0 && (
              <Form.Item label="Trading Coin-M Sessions">
                {tradingSiblings
                  ?.filter((s) => isCoinFuture(s.exchange))
                  .sort((a, b) => naturalSortSessions(a.account, b.account))
                  .map(({ account, balanceAllocationRatio, symbol }) => (
                    <span key={account}>
                      {account} ({symbol}): {balanceAllocationRatio}%<br />
                    </span>
                  ))}
              </Form.Item>
            )
          : // Show USD-M Future Trading Sessions
            tradingSiblings.length > 0 && (
              <Form.Item label="Trading USD-M Sessions">
                {tradingSiblings
                  ?.filter((s) => isUsdsFuture(s.exchange))
                  .sort((a, b) => naturalSortSessions(a.account, b.account))
                  .map(({ account, balanceAllocationRatio }) => (
                    <span key={account}>
                      {account}: {balanceAllocationRatio}%<br />
                    </span>
                  ))}
              </Form.Item>
            )}

        {/* Idle Sessions */}
        {isCoinFutureMode
          ? // Show Coin-M Future Idle Sessions
            idleCoinSessions.length > 0 && (
              <Form.Item label="Available Coin-M Sessions">
                {Array.from(new Set(idleCoinSessions.map((s) => s.account)))
                  .sort((a, b) => naturalSortSessions(a, b))
                  .map((account) => {
                    const sessions = idleCoinSessions.filter((s) => s.account === account);
                    return (
                      <span key={account}>
                        {account}: {sessions.map((s) => s.symbol).join(", ")}
                        <br />
                      </span>
                    );
                  })}
              </Form.Item>
            )
          : // Show USD-M Future Idle Sessions
            idleUsdSessions.length > 0 && (
              <Form.Item label="Available USD-M Sessions">
                {Array.from(new Set(idleUsdSessions.map((s) => s.account)))
                  .sort((a, b) => naturalSortSessions(a, b))
                  .map((account) => (
                    <span key={account}>
                      {account}
                      <br />
                    </span>
                  ))}
              </Form.Item>
            )}
        {isStateManagerModeSupported && (
          <Form.Item name={TRADE_FORM_FIELDS.stateManagerMode} label="stateManagerMode">
            <Select placeholder="Please select mode" onChange={() => {}}>
              {[
                { value: "standalone", label: "enigma_classic_standalone" },
                { value: "standalone-rule-based-portfolio-optimization-v1", label: "standalone-rule-based-portfolio-optimization-v1" },
                // { value: "optimizer", label: "optimizer" },
                ...ALPHA_FORGE_MODES.map((mode) => ({ value: mode, label: mode })),
              ].map(({ value, label }) => (
                <Option key={value} value={value}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
        {circuitBreakerIsSupported && (
          <>
            <Form.Item
              name={TRADE_FORM_FIELDS.enableCircuitBreaker}
              label={
                <>
                  Enable Circuit Breaker
                  <Tooltip
                    title="The circuit breaker is similar to trailing stop loss. If any percentage of loss threshold is reached, the system will stop trading and close position."
                    color="#78909c"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                </>
              }
            >
              <Select
                placeholder="Choose whether to use circuit breaker"
                value={enableCircuitBreaker}
                onChange={(value) => setEnableCircuitBreaker(value)}
              >
                {["true", "false"].map((option) => (
                  <Option key={option} value={option}>
                    {option}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {enableCircuitBreaker === "true" && (
              <Form.Item label="Breaker Thresholds" style={{ marginBottom: 0 }}>
                <span
                  style={{
                    display: "inline-block",
                    lineHeight: "32px",
                    textAlign: "center",
                    marginRight: 3,
                  }}
                >
                  Day:
                </span>
                <Form.Item
                  name={TRADE_FORM_FIELDS.circuitBreakerDailyThreshold}
                  style={{ display: "inline-block", width: "calc(33.3% - 44px)" }}
                >
                  <InputNumber
                    min={5}
                    max={99}
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace("%", "")}
                  />
                </Form.Item>
                <span
                  style={{
                    display: "inline-block",
                    lineHeight: "32px",
                    textAlign: "center",
                    marginLeft: 3,
                    marginRight: 3,
                  }}
                >
                  Week:
                </span>
                <Form.Item
                  name={TRADE_FORM_FIELDS.circuitBreakerWeeklyThreshold}
                  style={{ display: "inline-block", width: "calc(33.3% - 44px)" }}
                >
                  <InputNumber
                    min={5}
                    max={99}
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace("%", "")}
                  />
                </Form.Item>
                <span
                  style={{
                    display: "inline-block",
                    lineHeight: "32px",
                    textAlign: "center",
                    marginLeft: 3,
                    marginRight: 3,
                  }}
                >
                  Month:
                </span>
                <Form.Item
                  name={TRADE_FORM_FIELDS.circuitBreakerMonthlyThreshold}
                  style={{ display: "inline-block", width: "calc(33.3% - 44px)" }}
                >
                  <InputNumber
                    min={5}
                    max={99}
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}%`}
                    parser={(value) => value.replace("%", "")}
                  />
                </Form.Item>
              </Form.Item>
            )}
          </>
        )}
        {/* ------------- Advanced Options Section Starts ------------- */}
        <div hidden={!isAdvancedOptionsVisible}>
          {shouldShowTradingHoursRatio && (
            <Form.Item
              label={
                <>
                  Trading Hours Ratio
                  <Tooltip title="Overwrite model-specific trading and non-trading hours ratios." color="#78909c">
                    <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                </>
              }
              style={{ marginBottom: 16 }}
            >
              <div style={{ border: "1px solid #d9d9d9", borderRadius: "6px", padding: "12px" }}>
                <Form.List name={TRADE_FORM_FIELDS.tradingHoursParams}>
                  {(fields, { add, remove }) => (
                    <>
                      {/* Header Row */}
                      {fields.length > 0 && (
                        <Row gutter={8} style={{ marginBottom: 8 }}>
                          <Col span={8}>Model Type</Col>
                          <Col span={7}>Trading Hours</Col>
                          <Col span={7}>Non-Trading Hours</Col>
                          <Col span={2}></Col>
                        </Row>
                      )}

                      {/* Data Rows */}
                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "modelType"]}
                              rules={[{ required: true, message: "Model type is required" }]}
                            >
                              <Select style={{ width: "100%" }} mode="combobox" placeholder="Select or enter model type">
                                <Option value={MODEL_TYPE.lstm}>{MODEL_TYPE.lstm}</Option>
                                <Option value={MODEL_TYPE.transformer}>{MODEL_TYPE.transformer}</Option>
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={7}>
                            <Form.Item
                              {...restField}
                              name={[name, "tradingHoursRatio"]}
                              rules={[{ required: true, message: "Trading hours ratio is required" }]}
                            >
                              <InputNumber
                                min={0}
                                max={100}
                                step={10}
                                style={{ width: "100%" }}
                                formatter={(value) => `${value}%`}
                                parser={(value) => value.replace("%", "")}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={7}>
                            <Form.Item
                              {...restField}
                              name={[name, "nonTradingHoursRatio"]}
                              rules={[{ required: true, message: "Non-trading hours ratio is required" }]}
                            >
                              <InputNumber
                                min={0}
                                max={100}
                                step={10}
                                style={{ width: "100%" }}
                                formatter={(value) => `${value}%`}
                                parser={(value) => value.replace("%", "")}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button type="link" icon={<MinusCircleOutlined />} onClick={() => remove(name)} size="small" danger />
                          </Col>
                        </Row>
                      ))}

                      {/* Add Row / Clear All Buttons */}
                      <Row style={{ marginTop: 8 }}>
                        <Col span={fields.length === 0 ? 24 : 20} style={{ textAlign: "center" }}>
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              if (fields.length === 0) {
                                // Add both lstm and transformer by default when starting from empty
                                add({ modelType: MODEL_TYPE.lstm, tradingHoursRatio: 100, nonTradingHoursRatio: 0 });
                                add({ modelType: MODEL_TYPE.transformer, tradingHoursRatio: 100, nonTradingHoursRatio: 0 });
                              } else {
                                // Add single row when there are already existing rows
                                add({ modelType: "", tradingHoursRatio: 100, nonTradingHoursRatio: 0 });
                              }
                            }}
                            size="small"
                          >
                            Add Trading Hours Parameter
                          </Button>
                        </Col>
                        {fields.length > 0 && (
                          <Col span={4} style={{ textAlign: "right" }}>
                            <Button
                              type="link"
                              onClick={() => {
                                // Remove all fields by removing them in reverse order to avoid index issues
                                for (let i = fields.length - 1; i >= 0; i--) {
                                  remove(fields[i].name);
                                }
                              }}
                              size="small"
                            >
                              Clear All
                            </Button>
                          </Col>
                        )}
                      </Row>
                    </>
                  )}
                </Form.List>
              </div>
            </Form.Item>
          )}
          {!isArbitrageMode && exchange === EXCHANGE_INSTRUMENT.binance_usds_future && (
            <Form.Item label="Minimum BNB To Hold" name={TRADE_FORM_FIELDS.minimumBNB}>
              <InputNumber min={0} step={100} style={{ width: "100%" }} />
            </Form.Item>
          )}
          {!isArbitrageMode && (
            <Form.Item label="Override Model Intervals" style={{ marginBottom: 0 }}>
              <Form.Item name={TRADE_FORM_FIELDS.overrideModelIntervals} style={{ display: "inline-block", width: "20%" }}>
                <Switch checked={overrideModelIntervals} onChange={(value) => setOverrideModelIntervals(value)} />
              </Form.Item>
              <Form.Item name={TRADE_FORM_FIELDS.modelIntervals} style={{ display: "inline-block", width: "80%", marginLeft: "auto" }}>
                {(overrideModelIntervals === true || overrideModelIntervals === "true") && (
                  <Select mode="multiple" allowClear placeholder="Please select" defaultValue={[...availableTimeIntervals]}>
                    {availableTimeIntervals.map((interval) => (
                      <Option key={interval} value={interval}>
                        {interval}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
            </Form.Item>
          )}
          <Form.Item
            label={
              <>
                Proxy
                <Tooltip
                  title={
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        <strong>default</strong>: uses round robin based on session suffix (even number suffix uses proxy, odd doesn't)
                      </li>
                      <li>
                        <strong>none</strong>: don't use proxy
                      </li>
                      <li>
                        <strong>proxy1</strong>: always use proxy1
                      </li>
                    </ul>
                  }
                  color="#78909c"
                >
                  <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                </Tooltip>
              </>
            }
            name={TRADE_FORM_FIELDS.proxy}
          >
            <Select placeholder="Please select proxy" style={{ width: "100%" }}>
              <Option key="default" value="default">
                default
              </Option>
              <Option key="none" value="none">
                none
              </Option>
              <Option key="proxy1" value="proxy1">
                proxy1
              </Option>
            </Select>
          </Form.Item>
          {exchange === EXCHANGE_INSTRUMENT.binance_usds_future && (
            <Form.Item
              label={
                <>
                  Order Router
                  <Tooltip
                    title="Route orders through external execution service. Blockfill provides optimized execution with reduced slippage."
                    color="#78909c"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                </>
              }
              name={TRADE_FORM_FIELDS.orderRouter}
            >
              <Select placeholder="Default (direct exchange)" style={{ width: "100%" }} allowClear>
                <Option key="blockfill" value="blockfill">
                  blockfill
                </Option>
              </Select>
            </Form.Item>
          )}
          {!isArbitrageMode && (
            <Form.Item label="longRatio / shortRatio" style={{ marginBottom: 0 }}>
              <span
                style={{
                  display: "inline-block",
                  width: "24px",
                  lineHeight: "32px",
                  textAlign: "center",
                }}
              >
                L:
              </span>
              <Form.Item name={TRADE_FORM_FIELDS.longRatio} style={{ display: "inline-block", width: "calc(50% - 24px)" }}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={-100}
                  max={100}
                  step={10}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
              <span
                style={{
                  display: "inline-block",
                  width: "24px",
                  lineHeight: "32px",
                  textAlign: "center",
                }}
              >
                S:
              </span>
              <Form.Item name={TRADE_FORM_FIELDS.shortRatio} style={{ display: "inline-block", width: "calc(50% - 24px)" }}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={-100}
                  max={100}
                  step={10}
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                />
              </Form.Item>
            </Form.Item>
          )}
          {!isArbitrageMode && (
            <Form.Item
              label={
                <>
                  Collateral Ratio
                  <Tooltip
                    title="Multi-Asset Mode: determine how the system should value margined assets in terms of USD, as a minimum of two values."
                    color="#78909c"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                </>
              }
              labelCol={{ span: 7 }}
              style={{ marginBottom: 24 }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>min(</span>
                <span>MA(Day):</span>
                <Form.Item name={TRADE_FORM_FIELDS.ma} noStyle>
                  <InputNumber min={1} style={{ width: 80 }} placeholder="MA" />
                </Form.Item>
                <span>,</span>
                <span>Low(Day):</span>
                <Form.Item name={TRADE_FORM_FIELDS.low} noStyle>
                  <InputNumber min={1} style={{ width: 80 }} placeholder="Low" />
                </Form.Item>
                <span>)</span>
              </div>
            </Form.Item>
          )}
          <Form.Item label="loggerNamespaces" name={TRADE_FORM_FIELDS.loggerNamespaces}>
            <Select
              mode="tags"
              allowClear
              style={{
                width: "100%",
              }}
              placeholder="Enter 'all' to turn on all logger namespaces"
            />
          </Form.Item>

          {!isTradeAll && (
            <Form.Item
              label="Session Name"
              name={TRADE_FORM_FIELDS.sessionName}
              rules={[{ required: true, message: "Please input session name" }]}
            >
              <Input placeholder="Session name" />
            </Form.Item>
          )}
        </div>
        {/* ------------- Advanced Options Section Ends ------------- */}
        {/* ------------- Hidden Section Starts. Some old configs are not actively used, but may be needed in the future. Leave it hidden for now. ------------- */}
        <div hidden>
          <Form.Item name={TRADE_FORM_FIELDS.enableEonModelPredictor} label="Enable Eon-Predictor">
            <Select placeholder="Choose whether to use Eon Model Predictor">
              {["true", "false"].map((option) => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={TRADE_FORM_FIELDS.executeMode} label="executeMode">
            <Select placeholder="Please select mode" onChange={() => {}}>
              {["DEFAULT", "MARKETMAKING"].map((mode) => (
                <Option key={mode} value={mode}>
                  {mode}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={TRADE_FORM_FIELDS.executionDelay}
            label="executionDelay"
            rules={[{ pattern: /^[0-9]+s$/, message: "format error - see example: 0s | 10s" }]}
          >
            <Input placeholder="Please input executionDelay" />
          </Form.Item>
          <Form.Item name={TRADE_FORM_FIELDS.predictionSource} label="predictionSource">
            <Select placeholder="Please select prediction source" onChange={() => {}}>
              {["enigma", "samrat"].map((predictionSource) => (
                <Option key={predictionSource} value={predictionSource}>
                  {predictionSource}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={TRADE_FORM_FIELDS.contactNumbersStr}
            label="contactNumbersStr"
            rules={[
              {
                pattern: /^\+[0-9+ ]+$/,
                message: "Format error - follow example: +16044013923 +16044012699",
              },
            ]}
          >
            <Input placeholder="Please input contactNumbersStr" />
          </Form.Item>
        </div>
        {/* ------------- Hidden Options Section Ends ------------- */}
        <Row>
          <Col span={24} style={{ textAlign: "right" }}>
            <span className={styles.link} onClick={() => setAdvancedOptionsVisible(!isAdvancedOptionsVisible)}>
              {isAdvancedOptionsVisible ? <UpOutlined /> : <DownOutlined />} Advanced Options
            </span>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

function _createCMEForm(exchange, tradeModelDetails, pair) {
  const cmeForm = { formItems: <></>, initialValues: {} };
  if (
    exchange === EXCHANGE_INSTRUMENT.cme_tradestation_future ||
    tradeModelDetails.exchange === EXCHANGE_INSTRUMENT.cme_tradestation_future
  ) {
    cmeForm.initialValues = {
      [TRADE_FORM_FIELDS.leverage]: tradeModelDetails.leverage ?? 1,
      [TRADE_FORM_FIELDS.maxContractAmt]: tradeModelDetails.parsedConfigs?.maxContractAmt ?? undefined,
      [TRADE_FORM_FIELDS.extendedHoursRatio]: tradeModelDetails.parsedConfigs?.extendedHoursRatio ?? 100,
      [TRADE_FORM_FIELDS.dailyMarketClosedRatio]: tradeModelDetails.parsedConfigs?.dailyMarketClosedRatio ?? 100,
      [TRADE_FORM_FIELDS.weekendMarketClosedRatio]: tradeModelDetails.parsedConfigs?.weekendMarketClosedRatio ?? 0,
    };
    // prior to commodity trading, do not affect current crypto balance calculation
    const isFixedBalanceSupported = !["BTC/USD", "MBT/USD", "ETH/USD", "MET/USD"].includes(pair);
    if (isFixedBalanceSupported) {
      cmeForm.initialValues[TRADE_FORM_FIELDS.walletBalance] = tradeModelDetails.tradeAccountStatus?.walletBalance ?? 100000;
    }
    cmeForm.formItems = (
      <>
        {isFixedBalanceSupported && (
          <Form.Item
            name={TRADE_FORM_FIELDS.walletBalance}
            label="Fixed Session Balance"
            rules={[{ required: true, message: "CME needs a fixed balance for the session" }]}
          >
            <Input type="number" min={100000} step={100000} placeholder="session fixed balance" />
          </Form.Item>
        )}
        <Form.Item
          name={TRADE_FORM_FIELDS.leverage}
          label="Leverage"
          rules={[{ required: true, message: "CME only supports manual leverage settings" }]}
        >
          <Input type="number" min="1" max="4" placeholder="CME only supports manual leverage settings" />
        </Form.Item>
        <Form.Item
          name={TRADE_FORM_FIELDS.maxContractAmt}
          label={
            <>
              Max Contract Amt
              <Tooltip
                title="When provided, state is calculated as position/maxContractAmt instead of using wallet balance. Leverage and balance are ignored in this mode."
                color="#78909c"
              >
                <QuestionCircleOutlined style={{ marginLeft: 5 }} />
              </Tooltip>
            </>
          }
        >
          <InputNumber
            min={0.1}
            step={0.1}
            precision={2}
            style={{ width: "100%" }}
            placeholder="Optional: e.g., 4 means state=1 when holding 4 contracts"
          />
        </Form.Item>
        <Form.Item label="Market-Closed Position" style={{ marginBottom: 0 }} required>
          <span
            style={{
              display: "inline-block",
              lineHeight: "32px",
              textAlign: "center",
              marginRight: 3,
            }}
          >
            Extended Hours:
          </span>
          <Form.Item name={TRADE_FORM_FIELDS.extendedHoursRatio} style={{ display: "inline-block", width: "calc(33.3% - 72px)" }}>
            <InputNumber
              min={0}
              max={100}
              step={10}
              style={{ width: "100%" }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
          <span
            style={{
              display: "inline-block",
              lineHeight: "32px",
              textAlign: "center",
              marginLeft: 3,
              marginRight: 3,
            }}
          >
            Day:
          </span>
          <Form.Item name={TRADE_FORM_FIELDS.dailyMarketClosedRatio} style={{ display: "inline-block", width: "calc(33.3% - 72px)" }}>
            <InputNumber
              min={0}
              max={100}
              step={10}
              style={{ width: "100%" }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
          <span
            style={{
              display: "inline-block",
              lineHeight: "32px",
              textAlign: "center",
              marginLeft: 3,
              marginRight: 3,
            }}
          >
            Weekend:
          </span>
          <Form.Item name={TRADE_FORM_FIELDS.weekendMarketClosedRatio} style={{ display: "inline-block", width: "calc(33.3% - 72px)" }}>
            <InputNumber
              min={0}
              max={100}
              step={10}
              style={{ width: "100%" }}
              formatter={(value) => `${value}%`}
              parser={(value) => value.replace("%", "")}
            />
          </Form.Item>
        </Form.Item>
      </>
    );
  }
  return cmeForm;
}

export default TradeModal;

/**
 * Builds execution params from account data or form values
 * Used by both individual close position and bulk empty all
 * Mirrors the logic from executeTradeAction to ensure consistency
 */
export function buildTradeExecutionParams(source) {
  const F = TRADE_FORM_FIELDS;

  // Start with core required fields
  const params = {
    [F.exchange]: source.exchange || source[F.exchange],
    [F.account]: source.account || source[F.account],
    [F.pair]: source.symbol || source.pair || source[F.pair],
    [F.sessionName]: source.sessionName || source[F.sessionName],
  };

  // Merge all config data
  const allData = {
    ...(source.parsedConfigs || {}),
    ...(source.parsedModes || {}),
    ...source,
  };

  // Add all other fields, excluding form-only and mutation-unsupported
  Object.keys(F).forEach((fieldKey) => {
    const fieldName = F[fieldKey];
    const isExcluded = EXCLUDED_FROM_API.includes(fieldName) || MUTATION_UNSUPPORTED_FIELDS.includes(fieldName);
    const isAlreadyAdded = [F.exchange, F.account, F.pair, F.sessionName].includes(fieldName);

    if (!isExcluded && !isAlreadyAdded && allData[fieldName] !== undefined) {
      params[fieldName] = allData[fieldName];
    }
  });

  // Handle special formatting for multiAssetConfig
  if (allData[F.ma] !== undefined || allData[F.low] !== undefined) {
    params[F.multiAssetConfig] = formatMultiAssetConfig({
      ma: allData[F.ma],
      low: allData[F.low],
    });
  } else if (allData.multiAssetConfig) {
    params[F.multiAssetConfig] = formatMultiAssetConfig(allData.multiAssetConfig);
  }

  // Handle contact numbers array -> string
  if (source.contactNumbers?.length) {
    params[F.contactNumbersStr] = source.contactNumbers.join(",");
  }

  // Pass through JSON string params
  if (source.tradingHoursParams) {
    params[F.tradingHoursParams] = source.tradingHoursParams;
  }

  if (source.arbitrageParams) {
    params[F.arbitrageParams] = source.arbitrageParams;
  }

  return params;
}
